import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { Loader2, X } from 'lucide-react';

export function cx(...parts: (string | false | null | undefined)[]) {
  return parts.filter(Boolean).join(' ');
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent-500 text-white hover:bg-accent-400 disabled:bg-accent-600/40',
  secondary: 'bg-ink-700 text-ink-100 hover:bg-ink-600 disabled:bg-ink-800',
  ghost: 'bg-transparent text-ink-300 hover:bg-ink-800 hover:text-ink-100',
  danger: 'bg-red-600 text-white hover:bg-red-500 disabled:bg-red-900',
  success: 'bg-emerald-600 text-white hover:bg-emerald-500 disabled:bg-emerald-900',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading,
  icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  // 44px is Apple's minimum touch target and about the smallest thing a gloved
  // finger hits reliably. Applied by pointer type rather than width, so an iPad
  // gets it and a laptop keeps its density.
  const sizes = {
    sm: 'px-2.5 py-1.5 text-xs gap-1.5',
    md: 'px-3.5 py-2 text-sm gap-2 pointer-coarse:min-h-11',
    lg: 'px-5 py-3 text-base gap-2.5 pointer-coarse:min-h-12',
  };
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center rounded-lg font-medium transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950',
        'disabled:cursor-not-allowed disabled:opacity-60',
        sizes[size],
        VARIANTS[variant],
        className,
      )}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        {...rest}
        className={cx(
          'w-full rounded-lg border border-ink-600 bg-ink-800 px-3 py-2 text-sm text-ink-100',
          'placeholder:text-ink-400 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500',
          'disabled:opacity-50',
          className,
        )}
      />
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        {...rest}
        className={cx(
          'w-full rounded-lg border border-ink-600 bg-ink-800 px-3 py-2 text-sm text-ink-100',
          'placeholder:text-ink-400 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500',
          className,
        )}
      />
    );
  },
);

export function Select({ className, ...rest }: InputHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...(rest as object)}
      className={cx(
        'w-full rounded-lg border border-ink-600 bg-ink-800 px-3 py-2 text-sm text-ink-100',
        'focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500',
        className,
      )}
    />
  );
}

export function Badge({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'info';
  children: ReactNode;
  className?: string;
}) {
  const tones = {
    neutral: 'bg-ink-700 text-ink-300 border-ink-600',
    good: 'bg-emerald-950 text-emerald-300 border-emerald-800',
    warn: 'bg-amber-950 text-amber-300 border-amber-800',
    bad: 'bg-red-950 text-red-300 border-red-800',
    info: 'bg-sky-950 text-sky-300 border-sky-800',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cx('animate-spin text-ink-400', className)} size={20} />;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4">
      <div
        className={cx(
          'max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl border border-ink-700 bg-ink-900 shadow-2xl sm:rounded-2xl',
          wide ? 'sm:max-w-3xl' : 'sm:max-w-md',
        )}
        // A bottom sheet ends flush with the screen edge, which on a phone is
        // where the gesture bar lives — Cancel and Create end up underneath it.
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-ink-700 bg-ink-900 px-4 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-ink-700 px-6 py-12 text-center">
      {icon && <div className="mb-3 text-ink-400">{icon}</div>}
      <p className="text-sm font-medium text-ink-100">{title}</p>
      {body && <p className="mt-1 max-w-sm text-sm text-ink-400">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
